import { ShieldAlert } from 'lucide-react'
import { H2, Muted } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

type NoAccessMessageProps = {
  reason: 'billing-details' | 'payment-method'
  isSuperadmin: boolean
  onSkip: () => void
}

const COPY: Record<
  NoAccessMessageProps['reason'],
  { title: string; description: string }
> = {
  'billing-details': {
    title: 'Billing details required',
    description:
      'Sevalla needs valid billing details on file before this company can keep using resources. This is required for tax and compliance reasons. Please ask the company owner, an admin, or a billing user to add billing details from the Billing page.',
  },
  'payment-method': {
    title: 'Payment method required',
    description:
      'Sevalla needs a valid payment method on file before this company can use resources. Please ask the company owner, an admin, or a billing user to add a payment method from the Billing page.',
  },
}

export const NoAccessMessage = ({
  reason,
  isSuperadmin,
  onSkip,
}: NoAccessMessageProps) => {
  const { title, description } = COPY[reason]

  return (
    <div className="pb-25">
      <Card className="py-0 overflow-hidden">
        <div className="p-6 lg:p-8 flex flex-col gap-6">
          <div className="flex gap-4">
            <div
              aria-hidden="true"
              className="size-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0"
            >
              <ShieldAlert className="size-5 text-primary" />
            </div>
            <div className="flex flex-col gap-2 min-w-0">
              <H2 className="text-pretty">{title}</H2>
              <Muted className="text-pretty">{description}</Muted>
            </div>
          </div>
          {isSuperadmin ? (
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={onSkip}
              >
                Skip (superadmin)
              </Button>
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  )
}
